import React, { useCallback, useEffect, useMemo, useState } from "react";
import { starsOf, Hud } from "./nqShared";
import { sfx } from "./nqSound";

const CRYSTALS = [
  { id: 0, color: "#E0705C" }, { id: 1, color: "#F2B84B" }, { id: 2, color: "#7CC6A6" }, { id: 3, color: "#5E9BD6" }, { id: 4, color: "#8B6FC0" }, { id: 5, color: "#3FA7A3" },
];
const CFG = {
  1: { rounds: 3, lengths: [3, 3, 4], count: 4, speed: 800, distract: false },
  2: { rounds: 3, lengths: [5, 6, 7], count: 5, speed: 650, distract: false },
  3: { rounds: 3, lengths: [6, 7, 8], count: 6, speed: 550, distract: true },
};
const rand = (n) => Math.floor(Math.random() * n);

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Les cristaux s'allument dans un ordre secret",
    goal: "retenir l'ordre des cristaux et le rejouer.",
    regarde: "Les cristaux brillent un par un, dans un ordre secret. Ne touche à rien pendant qu'ils s'allument.",
    fais: "Quand tu vois « ✋ À toi ! », touche les cristaux dans le même ordre.",
    attention: `${c.rounds} séquences de ${c.lengths.join(", ")} cristaux.${c.distract ? " Un cristal gris fantôme clignote parfois : il ne compte pas !" : " Astuce : répète l'ordre dans ta tête pendant que tu regardes."}`,
    preview: { ok: ["💎"], ko: c.distract ? ["👻"] : [], okLabel: "Les cristaux allumés", koLabel: "Le fantôme" },
  };
};

export default function GameCristal({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { ...CFG[level.n], rounds: 1, lengths: [3], distract: false } : CFG[level.n]), [level, practice]);
  const [phase, setPhase] = useState("start"); // start | show | input | between
  const [round, setRound] = useState(0);
  const [seq, setSeq] = useState([]);
  const [lit, setLit] = useState(null);
  const [ghost, setGhost] = useState(false);
  const [input, setInput] = useState([]);
  const [results, setResults] = useState([]);
  const crystals = CRYSTALS.slice(0, cfg.count);

  const startRound = useCallback((r) => {
    const s = Array.from({ length: cfg.lengths[r] }, () => rand(cfg.count));
    setSeq(s); setInput([]); setPhase("show"); setRound(r);
    s.forEach((c, i) => {
      setTimeout(() => setLit(c), i * cfg.speed + 400);
      setTimeout(() => setLit(null), i * cfg.speed + 400 + cfg.speed * 0.6);
      if (cfg.distract && i % 2 === 1) { setTimeout(() => setGhost(true), i * cfg.speed + 400 + cfg.speed * 0.65); setTimeout(() => setGhost(false), i * cfg.speed + 400 + cfg.speed * 0.9); }
    });
    setTimeout(() => setPhase("input"), s.length * cfg.speed + 500);
  }, [cfg]);

  useEffect(() => { if (phase === "start") { const id = setTimeout(() => startRound(0), 600); return () => clearTimeout(id); } return undefined; }, [phase, startRound]);

  useEffect(() => {
    if (phase !== "between") return undefined;
    const done = results.length;
    if (done >= cfg.rounds) {
      if (practice) { onPracticeEnd(); return undefined; }
      const total = results.reduce((t, r) => t + r.len, 0);
      const hits = results.reduce((t, r) => t + r.correct, 0);
      const precision = Math.round((100 * hits) / total);
      const perfect = results.filter((r) => r.correct === r.len).length;
      onFinish({ hits, total, errors: total - hits, precision, stars: starsOf(precision), detail: `${perfect}/${cfg.rounds} séquences parfaites · ${hits}/${total} cristaux dans le bon ordre` });
      return undefined;
    }
    const id = setTimeout(() => startRound(done), 1400);
    return () => clearTimeout(id);
  }, [phase, results, cfg.rounds, startRound, onFinish, practice, onPracticeEnd]);

  const tap = (id) => {
    if (phase !== "input") return;
    const next = [...input, id];
    setLit(id); sfx.tick(); setTimeout(() => setLit(null), 180);
    setInput(next);
    if (next.length === seq.length) {
      const correct = next.filter((c, i) => c === seq[i]).length;
      if (correct === seq.length) sfx.ok(); else sfx.ko();
      setResults((r) => [...r, { len: seq.length, correct }]);
      setPhase("between");
    }
  };

  const last = results[results.length - 1];
  return (
    <div className="space-y-4" data-testid="nq-game-cristal">
      <Hud items={[["Séquence", `${Math.min(round + 1, cfg.rounds)}/${cfg.rounds}`, power.color], ["Cristaux", seq.length, "#5E9BD6"], ["Ta réponse", `${input.length}/${seq.length}`, "#7CC6A6"]]} />
      <div className="rounded-3xl bg-[#1F2A44] p-6 sm:p-8 text-center">
        <div className={`text-lg font-black mb-4 h-8 ${phase === "input" ? "text-[#F2B84B] animate-pulse" : "text-white/85"}`} data-testid="nq-cristal-status">
          {(phase === "show" || phase === "start") && "👀 Regarde bien…"}{phase === "input" && "✋ À toi ! Touche les cristaux dans le même ordre"}{phase === "between" && last && (last.correct === last.len ? "✨ Séquence parfaite !" : `${last.correct}/${last.len} dans le bon ordre — on continue !`)}
        </div>
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
          {crystals.map((c) => (
            <button key={c.id} onClick={() => tap(c.id)} disabled={phase !== "input"} data-testid={`nq-crystal-${c.id}`} aria-label={`cristal ${c.id + 1}`}
              className="w-16 h-20 sm:w-20 sm:h-24 transition-all duration-150 active:scale-95 disabled:cursor-default"
              style={{ clipPath: "polygon(50% 0%, 100% 30%, 82% 100%, 18% 100%, 0% 30%)", background: c.color, opacity: lit === c.id ? 1 : phase === "input" ? 0.55 : 0.35, transform: lit === c.id ? "scale(1.12)" : "scale(1)", boxShadow: lit === c.id ? `0 0 30px ${c.color}` : "none" }} />
          ))}
          {cfg.distract && <div data-testid="nq-crystal-ghost" className="w-16 h-20 sm:w-20 sm:h-24 transition-all duration-150" style={{ clipPath: "polygon(50% 0%, 100% 30%, 82% 100%, 18% 100%, 0% 30%)", background: "#9AA0A6", opacity: ghost ? 1 : 0.2, transform: ghost ? "scale(1.12)" : "scale(1)" }} />}
        </div>
        {phase === "input" && <div className="mt-4 flex justify-center gap-1.5" data-testid="nq-cristal-progress">{seq.map((_, i) => <span key={i} className={`w-3 h-3 rounded-full ${i < input.length ? "bg-[#F2B84B]" : "bg-white/25"}`} />)}</div>}
      </div>
    </div>
  );
}
